import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type KeyboardEvent } from 'react'
import { businessAppPeopleQuery, type CustomerPerson } from '../../api/businessApps'
import { toast } from '../../ui/notify'
import { personLabel } from '../OwnerPicker'
import { failed } from '../parts'
import {
  reportSettingsQuery,
  saveReportSettings,
  sendTestReport,
  type Frequency,
  type ReportSend,
  type ReportSettings,
  type ReportSettingsDraft,
} from './reportApi'
import './ReportSchedulePanel.css'

/**
 * "Automatic sending" - cardul trimiterii automate a raportului pe aplicatii.
 *
 * Coordonatoarea, 6 octombrie 2026: "Automatic sending ar trebui sa poti
 * selecta cand vrei, weekly / monthly, in ce zi si la ce ora si mai multe
 * persoane. Design mult mai frumos." Deci:
 *
 *  - sus, comutatorul On/Off si o propozitie in clar: cand pleaca, la cati,
 *    si urmatoarea trimitere (socotita de server dupa programul SALVAT);
 *  - Schedule: Weekly (ziua saptamanii) sau Monthly (ziua lunii, 1..28 sau
 *    "Last day"), ora plina, fusul orar (implicit Europe/Bucharest - firmele
 *    n-au fus al lor in baza). Weekly duce saptamana trecuta (luni-duminica),
 *    Monthly luna trecuta;
 *  - Recipients: "chips" cu x - persoanele de contact ale clientului, cautate
 *    ca in `OwnerPicker`, si adrese scrise de mana (validate pe loc; serverul
 *    le valideaza oricum). Se tin ca adrese: o persoana aleasa din lista isi
 *    pastreaza numele pe ecran cat timp adresa ei e a cuiva din lista;
 *  - "Send a test now", la mine sau la o adresa - prin acelasi postas, deci
 *    cu `BUSINESS_APPS_REPORT_SEND` si DIVERT respectate de server;
 *  - istoricul ultimelor trimiteri (`it_business_app_report_sends`).
 */

export const DAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: 'Mon', long: 'Monday' },
  { value: 2, short: 'Tue', long: 'Tuesday' },
  { value: 3, short: 'Wed', long: 'Wednesday' },
  { value: 4, short: 'Thu', long: 'Thursday' },
  { value: 5, short: 'Fri', long: 'Friday' },
  { value: 6, short: 'Sat', long: 'Saturday' },
  { value: 7, short: 'Sun', long: 'Sunday' },
]

const ZONES = ['Europe/Bucharest', 'Europe/Chisinau', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'UTC']

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim())
}

function ordinal(n: number): string {
  const tail = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')
  return `${n}${tail}`
}

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`

/** "every Monday at 08:00", "on the 1st of every month at 08:00", "on the last day of every month at 18:00". */
export function scheduleText(d: Pick<ReportSettingsDraft, 'frequency' | 'dayOfWeek' | 'dayOfMonth' | 'hour'>): string {
  const at = hourLabel(d.hour)
  if (d.frequency === 'weekly') return `every ${DAYS.find((x) => x.value === d.dayOfWeek)?.long ?? 'Monday'} at ${at}`
  if (d.dayOfMonth === 0) return `on the last day of every month at ${at}`
  return `on the ${ordinal(d.dayOfMonth)} of every month at ${at}`
}

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`)

/**
 * Propozitia de sus. "Next" vine de la server si e a programului SALVAT - cu
 * schimbari nesalvate nu se arata, ca sa nu minta.
 */
export function summaryText(draft: ReportSettingsDraft, saved: ReportSettings, dirty: boolean): string {
  if (!draft.autoSend) return `Automatic sending is off. Turn it on to email this report ${scheduleText(draft)} (${draft.timezone}).`
  const head = `Sent ${scheduleText(draft)} to ${people(draft.recipients.length)}`
  if (dirty || !saved.autoSend || !saved.nextLabel) return `${head} (${draft.timezone}).`
  return `${head} - next: ${saved.nextLabel} (${draft.timezone}), with the report for ${saved.nextPeriod ?? 'the last period'}.`
}

function fromSaved(s: ReportSettings): ReportSettingsDraft {
  const sc = s.schedule
  return {
    autoSend: s.autoSend ?? false,
    recipients: s.recipients ?? [],
    frequency: sc?.frequency ?? 'monthly',
    dayOfWeek: sc?.dayOfWeek ?? 1,
    dayOfMonth: sc?.dayOfMonth ?? 1,
    hour: sc?.hour ?? 8,
    timezone: sc?.timezone ?? s.defaultTimezone ?? 'Europe/Bucharest',
  }
}

const same = (a: ReportSettingsDraft, b: ReportSettingsDraft) => JSON.stringify(a) === JSON.stringify(b)

const STATUS: Record<string, [string, string]> = {
  sent: ['Sent', 'rs-pill-ok'],
  failed: ['Failed', 'rs-pill-bad'],
  skipped: ['Skipped', 'rs-pill-muted'],
  claimed: ['Sending', 'rs-pill-info'],
}

export function ReportSchedulePanel({ company }: { company: number }) {
  const queryClient = useQueryClient()
  const settings = useQuery(reportSettingsQuery(company))
  const contacts = useQuery({ ...businessAppPeopleQuery(company), enabled: company > 0 })
  const [draft, setDraft] = useState<ReportSettingsDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  if (!settings.data) {
    return (
      <section className="rs-card am-noprint" id="mrSettings" aria-busy="true">
        <div className="rs-loading">Loading automatic sending...</div>
      </section>
    )
  }
  const s = settings.data
  if (!s.ready) {
    return (
      <section className="rs-card am-noprint" id="mrSettings">
        <div className="rs-note rs-note-warn">{s.message}</div>
      </section>
    )
  }
  const saved = fromSaved(s)
  const value = draft ?? saved
  const dirty = draft !== null && !same(draft, saved)
  const editable = s.canEdit === true
  const set = (patch: Partial<ReportSettingsDraft>) => {
    setProblem(null)
    setDraft({ ...value, ...patch })
  }
  const max = s.maxRecipients ?? 20
  const peopleList = (contacts.data?.people ?? []).filter((p) => (p.email ?? '').trim() !== '')

  const save = () => {
    if (value.autoSend && value.recipients.length === 0) {
      setProblem('Add at least one recipient before turning automatic sending on.')
      return
    }
    setSaving(true)
    saveReportSettings(company, value)
      .then((next) => {
        queryClient.setQueryData(reportSettingsQuery(company).queryKey, next)
        setDraft(null)
        setProblem(null)
        toast('Automatic sending saved.', 'Applications report')
      })
      .catch((error) => setProblem(failed(error)))
      .finally(() => setSaving(false))
  }

  return (
    <section className={`rs-card am-noprint${value.autoSend ? ' rs-on' : ''}`} id="mrSettings" aria-labelledby="rsTitle">
      <header className="rs-head">
        <span className="rs-icon" aria-hidden="true">
          <i className="fas fa-paper-plane"></i>
        </span>
        <div className="rs-head-text">
          <h2 id="rsTitle">Automatic sending</h2>
          <p className="rs-summary" id="mrSummary">
            {summaryText(value, s, dirty)}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          id="mrAutoSend"
          aria-checked={value.autoSend}
          aria-label="Send automatically"
          className={`rs-switch${value.autoSend ? ' is-on' : ''}`}
          disabled={!editable}
          onClick={() => set({ autoSend: !value.autoSend })}
        >
          <span className="rs-switch-track">
            <span className="rs-switch-knob"></span>
          </span>
          <span className="rs-switch-text">{value.autoSend ? 'On' : 'Off'}</span>
        </button>
      </header>

      {s.sendingEnabled === false && (
        <div className="rs-note rs-note-warn" id="mrServerOff">
          <i className="fas fa-power-off" aria-hidden="true"></i> Sending is switched off on this server, so no email leaves - neither the scheduled ones nor tests. The
          schedule is kept and starts working once an administrator turns sending on.
        </div>
      )}

      <div className="rs-grid">
        <fieldset className="rs-section" disabled={!editable}>
          <legend className="rs-section-title">
            <i className="far fa-clock" aria-hidden="true"></i> Schedule
          </legend>

          <div className="rs-field">
            <span className="rs-label" id="rsFreqLabel">
              Frequency
            </span>
            <div className="rs-seg" role="radiogroup" aria-labelledby="rsFreqLabel">
              {(['weekly', 'monthly'] as Frequency[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  role="radio"
                  id={`mrFreq-${f}`}
                  aria-checked={value.frequency === f}
                  className={value.frequency === f ? 'is-active' : undefined}
                  onClick={() => set({ frequency: f })}
                >
                  {f === 'weekly' ? 'Weekly' : 'Monthly'}
                </button>
              ))}
            </div>
          </div>

          {value.frequency === 'weekly' ? (
            <div className="rs-field">
              <span className="rs-label" id="rsDayLabel">
                Day of the week
              </span>
              <div className="rs-seg rs-seg-days" role="radiogroup" aria-labelledby="rsDayLabel">
                {DAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    role="radio"
                    id={`mrDow-${d.value}`}
                    aria-checked={value.dayOfWeek === d.value}
                    aria-label={d.long}
                    className={value.dayOfWeek === d.value ? 'is-active' : undefined}
                    onClick={() => set({ dayOfWeek: d.value })}
                  >
                    {d.short}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="rs-field">
              <label className="rs-label" htmlFor="mrDom">
                Day of the month
              </label>
              <select id="mrDom" className="form-control rs-select" value={value.dayOfMonth} onChange={(e) => set({ dayOfMonth: Number(e.target.value) })}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {ordinal(d)}
                  </option>
                ))}
                <option value={0}>Last day of the month</option>
              </select>
            </div>
          )}

          <div className="rs-field rs-field-row">
            <div>
              <label className="rs-label" htmlFor="mrHour">
                Time
              </label>
              <select id="mrHour" className="form-control rs-select" value={value.hour} onChange={(e) => set({ hour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <div className="rs-grow">
              <label className="rs-label" htmlFor="mrZone">
                Time zone
              </label>
              <select id="mrZone" className="form-control rs-select" value={value.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                {(ZONES.includes(value.timezone) ? ZONES : [value.timezone, ...ZONES]).map((z) => (
                  <option key={z} value={z}>
                    {z.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="rs-hint" id="mrPeriodHint">
            {value.frequency === 'weekly'
              ? 'Each email carries the report of the previous week, Monday to Sunday, as PDF.'
              : `Each email carries the report of the previous month, as PDF${value.dayOfMonth === 0 ? ' - also when sent on the last day' : ''}.`}
          </p>
        </fieldset>

        <fieldset className="rs-section" disabled={!editable}>
          <legend className="rs-section-title">
            <i className="fas fa-user-friends" aria-hidden="true"></i> Recipients <span className="rs-count">{value.recipients.length}</span>
          </legend>
          <RecipientsField
            value={value.recipients}
            people={peopleList}
            max={max}
            onChange={(recipients) => set({ recipients })}
          />
        </fieldset>
      </div>

      {problem && (
        <div className="rs-note rs-note-bad" role="alert" id="mrProblem">
          {problem}
        </div>
      )}

      {editable && (
        <div className="rs-actions">
          <button type="button" className="btn btn-success" id="mrSave" disabled={saving || !dirty} onClick={save}>
            <i className="fas fa-check" aria-hidden="true"></i> {saving ? 'Saving...' : 'Save'}
          </button>
          {dirty && (
            <button type="button" className="btn btn-link" id="mrDiscard" disabled={saving} onClick={() => {
                setDraft(null)
                setProblem(null)
              }}>
              Discard changes
            </button>
          )}
          <span className="rs-spacer"></span>
          <TestSend company={company} me={s.me ?? ''} frequency={value.frequency} serverOff={s.sendingEnabled === false} />
        </div>
      )}

      <History sends={s.sends ?? []} />
    </section>
  )
}

function RecipientsField({
  value,
  people,
  max,
  onChange,
}: {
  value: string[]
  people: CustomerPerson[]
  max: number
  onChange(next: string[]): void
}) {
  const [search, setSearch] = useState('')
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const byEmail = new Map(people.map((p) => [(p.email ?? '').trim().toLowerCase(), p]))
  const full = value.length >= max

  const add = (raw: string): boolean => {
    const emails = raw
      .split(/[\s,;]+/)
      .map((x) => x.trim().toLowerCase())
      .filter((x) => x !== '')
    if (emails.length === 0) return false
    const bad = emails.filter((x) => !isEmail(x))
    if (bad.length > 0) {
      setError(`Not a valid email address: ${bad.slice(0, 3).join(', ')}.`)
      return false
    }
    const next = [...value]
    for (const e of emails) if (!next.includes(e)) next.push(e)
    if (next.length > max) {
      setError(`At most ${max} recipients.`)
      return false
    }
    setError(null)
    onChange(next)
    return true
  }

  const q = search.trim().toLowerCase()
  const matches =
    q === ''
      ? []
      : people
          .filter((p) => !value.includes((p.email ?? '').trim().toLowerCase()))
          .filter((p) => [p.name, p.role ?? '', p.email ?? ''].some((x) => x.toLowerCase().includes(q)))
          .sort((a, b) => Number(b.isContact === true) - Number(a.isContact === true))
          .slice(0, 8)

  const onTypedKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault()
      if (add(typed)) setTyped('')
    }
  }

  return (
    <div className="rs-recipients">
      {value.length === 0 ? (
        <p className="rs-empty" id="mrNoRecipients">
          Nobody yet. Pick the customer&apos;s contacts below, or type an email address.
        </p>
      ) : (
        <ul className="rs-chips" id="mrRecipients" aria-label="Recipients">
          {value.map((email) => {
            const p = byEmail.get(email)
            return (
              <li key={email} className={`rs-chip${p ? ' rs-chip-person' : ''}`} title={email}>
                <span className="rs-chip-avatar" aria-hidden="true">
                  {(p?.name ?? email).trim().charAt(0).toUpperCase()}
                </span>
                <span className="rs-chip-text">
                  {p ? (
                    <>
                      <strong>{p.name}</strong> <span className="rs-chip-mail">{email}</span>
                    </>
                  ) : (
                    email
                  )}
                </span>
                <button type="button" className="rs-chip-x" aria-label={`Remove ${email}`} onClick={() => onChange(value.filter((x) => x !== email))}>
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="rs-add">
        <label className="rs-label" htmlFor="mrContactSearch">
          Customer contacts
        </label>
        <div className="rs-combo">
          <i className="fas fa-search rs-combo-icon" aria-hidden="true"></i>
          <input
            id="mrContactSearch"
            type="search"
            className="form-control"
            autoComplete="off"
            placeholder={people.length > 0 ? 'Search by name, role or email' : 'No contact with an email address yet'}
            disabled={people.length === 0 || full}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {q !== '' && (
            <ul className="rs-options" role="listbox" id="mrContactOptions">
              {matches.length === 0 ? (
                <li className="rs-option-none">Nobody matches &quot;{search.trim()}&quot; - type the address below instead.</li>
              ) : (
                matches.map((p) => (
                  <li key={p.id} role="option" aria-selected="false">
                    <button
                      type="button"
                      className="rs-option"
                      onClick={() => {
                        if (add(p.email ?? '')) setSearch('')
                      }}
                    >
                      <span className="rs-chip-avatar" aria-hidden="true">
                        {p.name.trim().charAt(0).toUpperCase()}
                      </span>
                      <span>{personLabel(p)}</span>
                      {p.isContact === true && <span className="rs-tag">contact</span>}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <label className="rs-label" htmlFor="mrEmailInput">
          Other email address
        </label>
        <div className="rs-inline">
          <input
            id="mrEmailInput"
            type="email"
            className="form-control"
            placeholder="name@company.com"
            disabled={full}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value)
              setError(null)
            }}
            onKeyDown={onTypedKey}
          />
          <button type="button" className="btn btn-default" id="mrEmailAdd" disabled={full || typed.trim() === ''} onClick={() => {
              if (add(typed)) setTyped('')
            }}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add
          </button>
        </div>
        {error && (
          <p className="rs-field-error" role="alert" id="mrEmailError">
            {error}
          </p>
        )}
        {full && <p className="rs-hint">That is the most this report can go to ({max}).</p>}
      </div>
    </div>
  )
}

function TestSend({ company, me, frequency, serverOff }: { company: number; me: string; frequency: Frequency; serverOff: boolean }) {
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const send = () => {
    const target = to.trim()
    if (target !== '' && !isEmail(target)) {
      setResult({ ok: false, text: `Not a valid email address: ${target}.` })
      return
    }
    setBusy(true)
    setResult(null)
    sendTestReport(company, target, frequency)
      .then((r) => setResult({ ok: true, text: r.message }))
      .catch((error) => setResult({ ok: false, text: failed(error) }))
      .finally(() => setBusy(false))
  }

  return (
    <div className="rs-test">
      <div className="rs-inline">
        <input
          id="mrTestTo"
          type="email"
          className="form-control input-sm"
          aria-label="Send a test to"
          placeholder={me !== '' ? `to me (${me})` : 'name@company.com'}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-default btn-sm"
          id="mrTest"
          disabled={busy}
          title={serverOff ? 'Sending is switched off on this server' : undefined}
          onClick={send}
        >
          <i className="far fa-envelope" aria-hidden="true"></i> {busy ? 'Sending...' : 'Send a test now'}
        </button>
      </div>
      {result && (
        <p className={result.ok ? 'rs-test-ok' : 'rs-field-error'} role="status" id="mrTestResult">
          {result.text}
        </p>
      )}
    </div>
  )
}

function History({ sends }: { sends: ReportSend[] }) {
  return (
    <div className="rs-history">
      <h3 className="rs-section-title">
        <i className="fas fa-history" aria-hidden="true"></i> Recent sends
      </h3>
      {sends.length === 0 ? (
        <p className="rs-empty">Nothing sent yet.</p>
      ) : (
        <table className="rs-table" id="mrSends">
          <thead>
            <tr>
              <th>When</th>
              <th>Period</th>
              <th>Recipients</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sends.map((x) => {
              const [label, cls] = STATUS[x.status] ?? [x.status, 'rs-pill-muted']
              return (
                <tr key={x.period}>
                  <td>{x.sentAt ? x.sentAt.slice(0, 16) : '-'}</td>
                  <td>{x.periodLabel ?? x.period}</td>
                  <td>{x.status === 'sent' ? `${x.sentCount}${x.recipients && x.recipients !== x.sentCount ? ` of ${x.recipients}` : ''}` : (x.recipients ?? '-')}</td>
                  <td>
                    <span className={`rs-pill ${cls}`}>{label}</span>
                    {x.error && <div className="rs-error-line">{x.error}</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
